// Runtime error monitoring — collects client/server error context and reports
// it to the backend monitoring endpoint. Reporting never breaks the user flow.
import ApiEndPoints from "@/constants/apiEndpoints";
import { buildApiUrl, getApiBaseUrl } from "@/lib/api/baseUrl";

type RuntimeErrorSource =
  | "next-route-error"
  | "next-global-error"
  | "react-error-boundary"
  | "window-onerror"
  | "unhandled-rejection"
  | "proxy"
  | "server-action";

type SerializableContext =
  string | number | boolean | null | SerializableContext[] | { [key: string]: SerializableContext };

export type RuntimeErrorReportInput = {
  source: RuntimeErrorSource;
  error?: unknown;
  message?: string;
  digest?: string | null;
  pathname?: string | null;
  requestId?: string;
  context?: Record<string, SerializableContext>;
};

type RuntimeErrorPayload = {
  request_id: string;
  source: RuntimeErrorSource;
  message: string;
  digest?: string;
  runtime?: "client" | "server";
  url?: string;
  pathname?: string;
  referrer?: string;
  origin?: string;
  error_name?: string;
  top_frame?: string;
  timestamp?: string;
  release?: string;
  environment?: string;
  stack?: string;
  user_agent?: string;
  context?: Record<string, SerializableContext>;
};

function generateRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `runtime-${Date.now()}`;
}

function extractTopFrame(stack?: string): string | undefined {
  if (!stack) {
    return undefined;
  }

  const line = stack
    .split("\n")
    .map((item) => item.trim())
    .find((item, index) => index > 0 && item.length > 0);

  return line || undefined;
}

function canAccessStorage(kind: "localStorage" | "sessionStorage"): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return typeof window[kind] !== "undefined";
  } catch {
    return false;
  }
}

function normalizeError(error: unknown): {
  message: string;
  stack?: string;
  errorName?: string;
  topFrame?: string;
} {
  if (error instanceof Error) {
    return {
      message: error.message || error.name || "Unknown runtime error",
      stack: error.stack || undefined,
      errorName: error.name || undefined,
      topFrame: extractTopFrame(error.stack),
    };
  }

  if (typeof error === "string") {
    return { message: error };
  }

  try {
    return { message: JSON.stringify(error) };
  } catch {
    return { message: "Unknown runtime error" };
  }
}

function collectClientContext(): Record<string, SerializableContext> {
  if (typeof window === "undefined") {
    return {};
  }

  const nav = window.navigator;
  const screenInfo = window.screen;
  const connection =
    nav && "connection" in nav ? (nav.connection as Record<string, unknown>) : null;

  return {
    document_ready_state: typeof document !== "undefined" ? document.readyState : null,
    document_visibility_state: typeof document !== "undefined" ? document.visibilityState : null,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      pixel_ratio: window.devicePixelRatio || 1,
    },
    screen: screenInfo
      ? {
          width: screenInfo.width,
          height: screenInfo.height,
          avail_width: screenInfo.availWidth,
          avail_height: screenInfo.availHeight,
        }
      : null,
    browser: {
      language: nav?.language || null,
      languages: Array.isArray(nav?.languages) ? nav.languages.slice(0, 10) : [],
      cookie_enabled: nav?.cookieEnabled ?? null,
      online: nav?.onLine ?? null,
    },
    storage_access: {
      local_storage: canAccessStorage("localStorage"),
      session_storage: canAccessStorage("sessionStorage"),
    },
    connection: connection
      ? {
          effective_type:
            typeof connection.effectiveType === "string"
              ? (connection.effectiveType as string)
              : null,
          downlink: typeof connection.downlink === "number" ? connection.downlink : null,
          rtt: typeof connection.rtt === "number" ? connection.rtt : null,
          save_data: typeof connection.saveData === "boolean" ? connection.saveData : null,
        }
      : null,
    timezone:
      typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone || null : null,
  };
}

function collectServerContext(): Record<string, SerializableContext> {
  return {
    server_runtime: process.env.NEXT_RUNTIME || "nodejs",
    node_version: (() => {
      try {
        return process.version ?? null;
      } catch {
        return null;
      }
    })(),
  };
}

export function buildRuntimeErrorPayload(input: RuntimeErrorReportInput): RuntimeErrorPayload {
  const normalizedError = normalizeError(input.error);
  const runtime = typeof window === "undefined" ? "server" : "client";
  const pathname =
    input.pathname ||
    (typeof window !== "undefined" && window.location ? window.location.pathname : undefined) ||
    undefined;
  const userAgent =
    typeof window !== "undefined" && window.navigator
      ? window.navigator.userAgent || undefined
      : undefined;
  const url =
    typeof window !== "undefined" && window.location
      ? window.location.href || undefined
      : undefined;
  const referrer = typeof document !== "undefined" ? document.referrer || undefined : undefined;
  const origin =
    typeof window !== "undefined" && window.location
      ? window.location.origin || undefined
      : undefined;
  const release =
    process.env.NEXT_PUBLIC_APP_VERSION ||
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ||
    process.env.NEXT_PUBLIC_GIT_SHA ||
    undefined;
  const environment = process.env.NEXT_PUBLIC_APP_ENV || process.env.NODE_ENV || undefined;
  const autoContext = runtime === "client" ? collectClientContext() : collectServerContext();

  return {
    request_id: input.requestId || generateRequestId(),
    source: input.source,
    message: input.message || normalizedError.message,
    digest: input.digest || undefined,
    runtime,
    url,
    pathname,
    referrer,
    origin,
    error_name: normalizedError.errorName,
    top_frame: normalizedError.topFrame,
    timestamp: new Date().toISOString(),
    release,
    environment,
    stack: normalizedError.stack,
    user_agent: userAgent,
    context: {
      ...autoContext,
      ...input.context,
    },
  };
}

// Browser security restrictions on third-party contexts (incognito, sandboxed
// iframes, blocked cookies, ITP, etc.) routinely raise these errors. They are
// not actionable on our side, so we filter them out of the monitoring pipeline.
const NOISY_ERROR_PATTERNS: RegExp[] = [
  /Failed to read the 'sessionStorage' property from 'Window'/i,
  /Failed to read the 'localStorage' property from 'Window'/i,
  /SecurityError.*storage/i,
  /QuotaExceededError.*storage/i,
  /Loading chunk \d+ failed/i,
];

function isNoisyError(message: string): boolean {
  return NOISY_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

export async function reportRuntimeError(input: RuntimeErrorReportInput): Promise<void> {
  const payload = buildRuntimeErrorPayload(input);

  if (isNoisyError(payload.message)) {
    return;
  }

  const baseUrl = getApiBaseUrl();
  const url = buildApiUrl(ApiEndPoints.MONITORING.FRONTEND_ERRORS, baseUrl);

  try {
    await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      cache: "no-store",
      ...(typeof window !== "undefined" ? { keepalive: true } : {}),
    });
  } catch {
    // Reporting must never break the user flow.
  }
}
